import { Icons } from '@/components/icons';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';
import type { GlobalFilterItem } from '../types';

const easeOut: [number, number, number, number] = [0.23, 1, 0.32, 1];

export function FilteredItems({
  items,
  toggleFilteredItem,
  clearAll,
}: {
  items: GlobalFilterItem[];
  toggleFilteredItem: (item: GlobalFilterItem) => void;
  clearAll: () => void;
}) {
  const childRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const childSize = useResizeObserver({
    ref: childRef as any,
    box: 'border-box',
  });

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{
        height: childRef.current ? childSize.height : 'auto',
        originY: 0,
        opacity: 1,
      }}
      exit={{ height: 0, opacity: 0 }}
      className='flex flex-col overflow-hidden border-b bg-surface'
    >
      <div ref={childRef} className='flex justify-between gap-1'>
        <div className='scrollbar-hide flex max-h-24 w-full flex-wrap items-center gap-y-1 overflow-y-auto p-2 text-xs'>
          {/* Same box metrics as a chip (border + padding) so adding the
              first filter doesn't change the section height. */}
          {!items.length && (
            <div className='flex items-center justify-center rounded-sm border border-transparent px-2 py-1.25 text-muted-foreground'>
              Inga filter valda
            </div>
          )}
          <AnimatePresence>
            {items.map((i) => (
              <motion.div
                key={i.id}
                className='mr-1 flex items-center rounded-sm border bg-muted px-2 py-1'
                initial={{
                  opacity: 0,
                  y: reduceMotion ? 0 : 4,
                  scale: reduceMotion ? 1 : 0.9,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { duration: 0.15, ease: easeOut },
                }}
                exit={{
                  opacity: 0,
                  scale: reduceMotion ? 1 : 0.9,
                  transition: { duration: 0.15, ease: easeOut },
                }}
              >
                <div className='text-foreground/70'>
                  {i.type === 'global_realestates'
                    ? 'Fastighet'
                    : i.type === 'global_segments'
                      ? 'Segment'
                      : i.type === 'filter_external_clients'
                        ? 'Uppdragsgivare'
                        : 'Bolag'}
                </div>
                <div className='mx-2 min-h-4 min-w-px bg-subtle-foreground/15' />
                <div>{i.displayString}</div>
                <button
                  onClick={() => toggleFilteredItem(i)}
                  className='ml-1 rounded-sm border bg-muted transition-[background-color,color,scale] hover:bg-destructive hover:text-background active:scale-[0.97]'
                >
                  <Icons.close className='size-4' />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        {!!items.length && (
          <div className='flex items-end justify-end p-2'>
            <button
              onClick={clearAll}
              className='flex shrink-0 items-center text-xs transition-[color,scale] hover:text-destructive active:scale-[0.97]'
            >
              Rensa alla <Icons.close className='ml-1 size-3' />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
